import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { adminAPI } from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, Plus, Edit2, Trash2, ChevronDown, ChevronRight, X, Save,
  Tag, Layers, AlertTriangle
} from 'lucide-react';
import { IconPicker, SectorIcon } from '@/components/IconPicker';

import { JOB_CATEGORY_SLUGS as JOB_CATEGORY_OPTIONS } from '@/constants/jobCategories';

export default function AdminCategories() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(null);

  // Modals
  const [catModal, setCatModal] = useState(null);   // {mode:'create'|'edit', data}
  const [suggModal, setSuggModal] = useState(null); // {slug, index?, data}
  const [confirm, setConfirm] = useState(null);     // {type, slug, index?, label}
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user && user.role !== 'admin') navigate('/dashboard');
  }, [user, navigate]);

  const load = useCallback(async () => {
    setError('');
    try {
      const r = await adminAPI.listCategories();
      setCategories(r.data.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || t('adminCategories.errorLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const saveCategory = async () => {
    if (!catModal) return;
    setSaving(true);
    setError('');
    try {
      const { mode, data } = catModal;
      if (mode === 'create') await adminAPI.createCategory(data);
      else await adminAPI.updateCategory(data.slug, data);
      setCatModal(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || t('adminCategories.errorSaveCategory'));
    } finally {
      setSaving(false);
    }
  };

  const saveSuggestion = async () => {
    if (!suggModal) return;
    setSaving(true);
    setError('');
    try {
      const { slug, index, data } = suggModal;
      if (index === undefined) await adminAPI.addSuggestion(slug, data);
      else await adminAPI.updateSuggestion(slug, index, data);
      setSuggModal(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || t('adminCategories.errorSaveSuggestion'));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!confirm) return;
    setSaving(true);
    setError('');
    try {
      if (confirm.type === 'category') await adminAPI.deleteCategory(confirm.slug);
      else await adminAPI.deleteSuggestion(confirm.slug, confirm.index);
      setConfirm(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || t('adminCategories.errorDelete'));
    } finally {
      setSaving(false);
    }
  };

  if (user && user.role !== 'admin') return null;

  return (
    <div className="min-h-screen bg-gray-50 py-10" data-testid="admin-categories">
      <div className="max-w-5xl mx-auto px-4">
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Layers className="h-6 w-6 text-blue-700" />
              <h1 className="text-3xl font-bold">{t('adminCategories.title')}</h1>
            </div>
            <p className="text-gray-600 text-sm">
              {t('adminCategories.subtitle')}
            </p>
          </div>
          <Button
            onClick={() => setCatModal({ mode: 'create', data: { name_pt: '', name_en: '', name_fr: '', icon: 'Briefcase', order: 999 } })}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="add-category-btn"
          >
            <Plus className="h-4 w-4 mr-2" />{t('adminCategories.newCategory')}
          </Button>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded bg-red-50 text-red-700 text-sm border border-red-200">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-16 text-gray-500">{t('adminCategories.loading')}</div>
        ) : (
          <div className="space-y-3">
            {categories.length === 0 && (
              <Card className="p-8 text-center text-gray-500">{t('adminCategories.noCategories')}</Card>
            )}
            {categories.map((c) => {
              const isOpen = expanded === c.slug;
              return (
                <Card key={c.slug} className="overflow-hidden" data-testid={`category-row-${c.slug}`}>
                  <div className="p-4 flex items-center gap-3 flex-wrap">
                    <button
                      className="flex items-center gap-2 flex-1 min-w-[200px] text-left"
                      onClick={() => setExpanded(isOpen ? null : c.slug)}
                      data-testid={`toggle-${c.slug}`}
                    >
                      {isOpen ? <ChevronDown className="h-4 w-4 text-gray-500" /> : <ChevronRight className="h-4 w-4 text-gray-500" />}
                      <span className="h-7 w-7 rounded-md bg-blue-50 text-blue-700 flex items-center justify-center shrink-0" data-testid={`cat-icon-${c.slug}`}>
                        <SectorIcon name={c.icon} className="h-4 w-4" />
                      </span>
                      <span className="font-semibold text-gray-900">{c.name_pt}</span>
                      <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-200 text-xs">
                        {t('adminCategories.suggestionsCount', { count: c.job_suggestions?.length || 0 })}
                      </Badge>
                    </button>
                    <div className="text-xs text-gray-500 flex items-center gap-3">
                      <span>slug: <code className="bg-gray-100 px-1 rounded">{c.slug}</code></span>
                      <span>#{c.order}</span>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => setCatModal({ mode: 'edit', data: { ...c } })}
                        data-testid={`edit-cat-${c.slug}`}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost" size="sm"
                        className="text-red-600 hover:bg-red-50 hover:text-red-700"
                        onClick={() => setConfirm({ type: 'category', slug: c.slug, label: c.name_pt })}
                        data-testid={`del-cat-${c.slug}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="border-t bg-gray-50 p-4 space-y-2">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs uppercase tracking-wide font-semibold text-gray-500">{t('adminCategories.jobSuggestions')}</p>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSuggModal({ slug: c.slug, data: { title_pt: '', job_category: 'other', description_template_pt: '' } })}
                          data-testid={`add-sugg-${c.slug}`}
                        >
                          <Plus className="h-4 w-4 mr-1" />{t('adminCategories.newSuggestion')}
                        </Button>
                      </div>
                      {(c.job_suggestions || []).length === 0 ? (
                        <p className="text-sm text-gray-500 italic">{t('adminCategories.noSuggestions')}</p>
                      ) : c.job_suggestions.map((s, i) => (
                        <div key={i} className="bg-white rounded-md border p-3 flex items-start gap-2">
                          <Tag className="h-4 w-4 text-blue-600 mt-1 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900">{s.title_pt}</p>
                            <p className="text-xs text-gray-500">{t('adminCategories.jobCategoryLabel')}: {s.job_category}</p>
                            {s.description_template_pt && (
                              <p className="text-xs text-gray-600 mt-1 line-clamp-2">{s.description_template_pt}</p>
                            )}
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => setSuggModal({ slug: c.slug, index: i, data: { ...s } })}
                              data-testid={`edit-sugg-${c.slug}-${i}`}
                            >
                              <Edit2 className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost" size="sm"
                              className="text-red-600 hover:bg-red-50 hover:text-red-700"
                              onClick={() => setConfirm({ type: 'suggestion', slug: c.slug, index: i, label: s.title_pt })}
                              data-testid={`del-sugg-${c.slug}-${i}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        {/* Category modal */}
        {catModal && (
          <Modal onClose={() => !saving && setCatModal(null)}>
            <h3 className="text-lg font-semibold mb-4">
              {catModal.mode === 'create' ? t('adminCategories.modalNewCategory') : t('adminCategories.modalEditCategory')}
            </h3>
            <div className="space-y-3">
              <Field label={t('adminCategories.fieldNamePt')}>
                <Input
                  value={catModal.data.name_pt}
                  onChange={(e) => setCatModal({ ...catModal, data: { ...catModal.data, name_pt: e.target.value } })}
                  placeholder="Ex: Turismo Aventura"
                  data-testid="cat-name-input"
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t('adminCategories.fieldNameEn')}>
                  <Input
                    value={catModal.data.name_en || ''}
                    onChange={(e) => setCatModal({ ...catModal, data: { ...catModal.data, name_en: e.target.value } })}
                    data-testid="cat-name-en-input"
                  />
                </Field>
                <Field label={t('adminCategories.fieldNameFr')}>
                  <Input
                    value={catModal.data.name_fr || ''}
                    onChange={(e) => setCatModal({ ...catModal, data: { ...catModal.data, name_fr: e.target.value } })}
                    data-testid="cat-name-fr-input"
                  />
                </Field>
              </div>
              <Field label={t('adminCategories.fieldIcon')} hint={t('adminCategories.fieldIconHint')}>
                <IconPicker
                  value={catModal.data.icon || 'Briefcase'}
                  onChange={(icon) => setCatModal({ ...catModal, data: { ...catModal.data, icon } })}
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t('adminCategories.fieldOrder')} hint={t('adminCategories.fieldOrderHint')}>
                  <Input
                    type="number"
                    value={catModal.data.order ?? 999}
                    onChange={(e) => setCatModal({ ...catModal, data: { ...catModal.data, order: parseInt(e.target.value || '999', 10) } })}
                    data-testid="cat-order-input"
                  />
                </Field>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button variant="outline" className="flex-1" onClick={() => setCatModal(null)} disabled={saving}>{t('adminCategories.cancel')}</Button>
              <Button
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                onClick={saveCategory}
                disabled={saving || !catModal.data.name_pt.trim()}
                data-testid="save-cat-btn"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? t('adminCategories.saving') : t('adminCategories.save')}
              </Button>
            </div>
          </Modal>
        )}

        {/* Suggestion modal */}
        {suggModal && (
          <Modal onClose={() => !saving && setSuggModal(null)}>
            <h3 className="text-lg font-semibold mb-4">
              {suggModal.index === undefined ? t('adminCategories.modalNewSuggestion') : t('adminCategories.modalEditSuggestion')}
            </h3>
            <div className="space-y-3">
              <Field label={t('adminCategories.fieldJobTitlePt')}>
                <Input
                  value={suggModal.data.title_pt}
                  onChange={(e) => setSuggModal({ ...suggModal, data: { ...suggModal.data, title_pt: e.target.value } })}
                  placeholder="Ex: Rececionista"
                  data-testid="sugg-title-input"
                />
              </Field>
              <Field label={t('adminCategories.fieldJobCategory')}>
                <select
                  value={suggModal.data.job_category}
                  onChange={(e) => setSuggModal({ ...suggModal, data: { ...suggModal.data, job_category: e.target.value } })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  data-testid="sugg-job-category-select"
                >
                  {JOB_CATEGORY_OPTIONS.map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
              </Field>
              <Field label={t('adminCategories.fieldBaseDescription')} hint={t('adminCategories.fieldBaseDescriptionHint')}>
                <Textarea
                  rows={5}
                  value={suggModal.data.description_template_pt || ''}
                  onChange={(e) => setSuggModal({ ...suggModal, data: { ...suggModal.data, description_template_pt: e.target.value } })}
                  placeholder="Procuramos rececionista para..."
                  data-testid="sugg-desc-textarea"
                />
              </Field>
            </div>
            <div className="flex gap-2 mt-6">
              <Button variant="outline" className="flex-1" onClick={() => setSuggModal(null)} disabled={saving}>{t('adminCategories.cancel')}</Button>
              <Button
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                onClick={saveSuggestion}
                disabled={saving || !suggModal.data.title_pt.trim()}
                data-testid="save-sugg-btn"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? t('adminCategories.saving') : t('adminCategories.save')}
              </Button>
            </div>
          </Modal>
        )}

        {/* Confirm delete */}
        {confirm && (
          <Modal onClose={() => !saving && setConfirm(null)}>
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 rounded-full bg-red-100 text-red-700">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold">
                  {confirm.type === 'category' ? t('adminCategories.confirmDeleteCategory') : t('adminCategories.confirmDeleteSuggestion')}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  <strong>{confirm.label}</strong>
                  {confirm.type === 'category' && (
                    <span className="block mt-2 text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 text-xs">
                      {t('adminCategories.deleteCategoryWarning')}
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setConfirm(null)} disabled={saving}>{t('adminCategories.cancel')}</Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                onClick={doDelete}
                disabled={saving}
                data-testid="confirm-delete-btn"
              >
                {saving ? t('adminCategories.deleting') : t('adminCategories.confirmDelete')}
              </Button>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
}

function Modal({ children, onClose }) {
  const { t } = useTranslation();
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <Card className="max-w-lg w-full p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-700"
          aria-label={t('adminCategories.close')}
        >
          <X className="h-5 w-5" />
        </button>
        {children}
      </Card>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">{label}</label>
      {children}
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}
